import { B2COrderResponseFragment, LotType, SvLotType } from 'parkdepot-shared/gql/graphql'
import React, { useState } from 'react'
import { DateTime } from 'luxon'
import { CARPAY_STATUS, CarpayStatusType } from 'parkdepot-shared/models/database'
import { useHistory, useLocation } from 'react-router-dom'
import carpayPayLaterRoute from '@views/Carpay/PayLater/route.const'
import { carpayPaymentInputRoute } from '@views/Carpay/PaymentInput/route.const'
import { carpayPaymentEmailRoute } from '@views/Carpay/PaymentEmail/route.const'
import StayOrLeaveActions from './StayOrLeaveActions.component'
import PaymentOrDiscountActions from './PaymentOrDiscountActions.component'
import PayNowOrLaterActions from './PayNowOrLaterActions.component'

export enum CarpayOrderActionsStep {
    StayOrLeave = 'stayOrLeave',
    PayNowOrLater = 'payNowOrLater',
    PaymentOrDiscount = 'paymentOrDiscount',
    ErrorFlow = 'ERRORFLOW'
}

interface CarpayOrderActionsProps {
    orderData: B2COrderResponseFragment
    allowDiscountOption?: boolean
}

// TODO: Improve this in the future
const CarpayOrderActions = ({ orderData, allowDiscountOption = true }: CarpayOrderActionsProps) => {
    const { lot, order, scanned_codes, price } = orderData
    const history = useHistory()
    const location = useLocation()

    const queryParams = new URLSearchParams(location.search)
    const isFreeTime = !price.billable
    const startDate = DateTime.fromISO(order.start_date)
    const timeDiffSinceArrival = DateTime.now().diff(startDate, ['seconds'])
    const isInsideArrivalTime = timeDiffSinceArrival.seconds < (lot.arrival_detection_time ?? 900)
    const isPrepaid = lot.lot_type === LotType.Prepaid
    const isHybrid = lot.lot_type === LotType.Hybrid
    const isPostpaid = lot.lot_type === LotType.Postpaid
    const isTollRoadLot = lot.sv_lot_type === SvLotType.TollRoad
    // const thereArePriceContexts = parking_contexts.length > 0
    // const thereAreTariffs = lot.lot_tariffs.length > 0
    // const allowDiscount =
    //     !isFreeTime &&
    //     lot.discount_types.length > 0 &&
    //     scanned_codes.length === 0 &&
    //     !thereAreTariffs &&
    //     !thereArePriceContexts
    const allowDiscount = allowDiscountOption && !isFreeTime && scanned_codes.length === 0

    const [step, setStep] = useState<CarpayOrderActionsStep | null>(() => {
        // Special case -> Unpaid order -> user outside parking lot (pay right away)
        if (order.status === CARPAY_STATUS.UNPAID) return CarpayOrderActionsStep.PaymentOrDiscount

        // Prepay & Postpay
        if (isPrepaid || isPostpaid) {
            if (!isTollRoadLot) {
                if (isInsideArrivalTime) return CarpayOrderActionsStep.StayOrLeave
                if (isFreeTime) return null
            }
            return CarpayOrderActionsStep.PaymentOrDiscount
        }

        // Hybrid
        if (isHybrid) {
            return CarpayOrderActionsStep.StayOrLeave
        }

        // Default
        return CarpayOrderActionsStep.PaymentOrDiscount
    })

    const handlePay = () => {
        if (order.order_id === CarpayOrderActionsStep.ErrorFlow) {
            return history.push(`${carpayPaymentEmailRoute(order.order_id)}?${queryParams.toString()}`)
        }
        if (isHybrid || (isPrepaid && (isInsideArrivalTime || isFreeTime))) {
            return history.push(`${carpayPaymentInputRoute(order.order_id)}?${queryParams.toString()}`)
        }

        history.push(`${carpayPaymentEmailRoute(order.order_id)}?${queryParams.toString()}`)
    }

    const handleStay = () => {
        if (isHybrid) {
            setStep(CarpayOrderActionsStep.PayNowOrLater)
            return
        }

        if (isPostpaid) {
            // pay-later
            history.push(`${carpayPayLaterRoute}?plate=${order.plate}`)
            return
        }

        // prepaid
        setStep(CarpayOrderActionsStep.PaymentOrDiscount)
    }

    const handleLeave = () => {
        // *** Weird logic to avoid safari issue when clicking button: Focus remain applied on the following button ***
        // https://www.notion.so/Design-Review-Wemolo-App-dedf88f629404555a75f1938e303c87d?pvs=4#13f8cdb991428027bfbae6dc3f36d022
        if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
            setStep(null)

            setTimeout(() => {
                if (!isFreeTime) setStep(CarpayOrderActionsStep.PaymentOrDiscount)
            }, 10)

            return
        }
        // ********

        if (!isFreeTime) setStep(CarpayOrderActionsStep.PaymentOrDiscount)
        else setStep(null)
    }

    const handlePayNow = () => {
        handlePay()
        // setStep('paymentOrDiscount')
    }

    const handlePayLater = () => {
        history.push(`${carpayPayLaterRoute}?plate=${order.plate}`)
    }

    const orderStatus = order.status as CarpayStatusType

    if (['PAID', 'CANCELED', 'UNPAID_ENFORCEABLE'].includes(orderStatus)) {
        return null
    }

    if (isFreeTime && !isPrepaid && !isHybrid) {
        return null
    }

    if (step === CarpayOrderActionsStep.StayOrLeave)
        return <StayOrLeaveActions onLeave={handleLeave} onStay={handleStay} />
    if (step === CarpayOrderActionsStep.PayNowOrLater)
        return <PayNowOrLaterActions onPayLater={handlePayLater} onPayNow={handlePayNow} />
    if (step === CarpayOrderActionsStep.PaymentOrDiscount) {
        return <PaymentOrDiscountActions orderData={orderData} allowDiscount={allowDiscount} onPay={handlePay} />
    }

    return null
}

export default CarpayOrderActions
